import { useState, useEffect, useCallback } from 'react';
import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { DEFAULT_DATA } from './defaultData';
import './App.css';

const PIN_ADMIN = '1993';
const PIN_COADMIN = '2026';
const FIRESTORE_DOC = 'ops-dashboard/data';

function PinScreen({ onUnlockAdmin, onUnlockCoadmin }) {
  const [entry, setEntry] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const press = (d) => {
    if (entry.length >= 4) return;
    const next = entry + d;
    setEntry(next);
    setError('');
    if (next.length === 4) {
      setTimeout(() => {
        if (next === PIN_ADMIN) {
          onUnlockAdmin();
        } else if (next === PIN_COADMIN) {
          onUnlockCoadmin();
        } else {
          setShake(true);
          setError('Incorrect PIN');
          setEntry('');
          setTimeout(() => setShake(false), 500);
        }
      }, 150);
    }
  };

  const del = () => {
    setEntry(e => e.slice(0, -1));
    setError('');
  };

  return (
    <div className="pin-screen">
      <div className="pin-logo">
        <span className="pin-logo-l">L</span>itvill
        <span className="pin-logo-tag">ops</span>
      </div>
      <p className="pin-subtitle">Operations dashboard</p>
      <div className={`pin-dots ${shake ? 'shake' : ''}`}>
        {[0,1,2,3].map(i => (
          <div key={i} className={`pin-dot ${i < entry.length ? 'filled' : ''}`} />
        ))}
      </div>
      {error && <div className="pin-error">{error}</div>}
      {!error && <div className="pin-error"> </div>}
      <div className="pin-pad">
        {['1','2','3','4','5','6','7','8','9'].map(d => (
          <button key={d} className="pin-btn" onClick={() => press(d)}>{d}</button>
        ))}
        <div />
        <button className="pin-btn" onClick={() => press('0')}>0</button>
        <button className="pin-btn pin-del" onClick={del}>⌫</button>
      </div>
    </div>
  );
}

function CardRow({ row, revealed, onToggleReveal, editMode, onChange, onDelete, isReadOnly }) {
  const [copied, setCopied] = useState(false);

  const copy = (val) => {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  if (editMode) {
    return (
      <div className="row-edit">
        <input
          className="edit-input edit-label"
          value={row.label}
          onChange={e => onChange({ ...row, label: e.target.value })}
          placeholder="Label"
        />
        <select
          className="edit-select"
          value={row.type}
          onChange={e => onChange({ ...row, type: e.target.value })}
        >
          <option value="text">Text</option>
          <option value="link">Link</option>
          <option value="secret">Secret</option>
          <option value="color">Color</option>
        </select>
        <input
          className="edit-input edit-val"
          value={row.val}
          onChange={e => onChange({ ...row, val: e.target.value })}
          placeholder="Value"
        />
        <button className="row-del-btn" onClick={onDelete} title="Delete row">✕</button>
      </div>
    );
  }

  if (row.type === 'link') {
    const short = row.val.replace(/^https?:\/\//, '').slice(0, 32) + (row.val.length > 38 ? '…' : '');
    return (
      <div className="card-row">
        <span className="row-label">{row.label}</span>
        <a href={row.val} target="_blank" rel="noreferrer" className="row-link">
          {short} <span className="ext-icon">↗</span>
        </a>
      </div>
    );
  }

  if (row.type === 'secret') {
    const isShown = revealed && !isReadOnly;
    return (
      <div className="card-row">
        <span className="row-label">{row.label}</span>
        <div className="row-secret-wrap">
          <span className={`row-secret ${isShown ? 'shown' : ''}`}>{row.val}</span>
          {!isReadOnly && (
            <>
              <button className="icon-btn" onClick={onToggleReveal} title={isShown ? 'Hide' : 'Show'}>
                {isShown ? '🙈' : '👁'}
              </button>
              <button className="icon-btn" onClick={() => copy(row.val)} title="Copy">
                {copied ? '✓' : '⎘'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (row.type === 'color') {
    return (
      <div className="card-row">
        <span className="row-label">{row.label}</span>
        <div className="row-color-wrap">
          <div className="color-swatch" style={{ background: row.val }} />
          <span className="row-mono">{row.val}</span>
          <button className="icon-btn" onClick={() => copy(row.val)} title="Copy">
            {copied ? '✓' : '⎘'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-row">
      <span className="row-label">{row.label}</span>
      <span className="row-val">{row.val}</span>
    </div>
  );
}

function Card({ card, editMode, onUpdate, onDelete, isReadOnly }) {
  const [revealed, setRevealed] = useState({});

  const toggleReveal = (idx) => {
    setRevealed(r => ({ ...r, [idx]: !r[idx] }));
  };

  const updateRow = (idx, newRow) => {
    const rows = card.rows.map((r, i) => i === idx ? newRow : r);
    onUpdate({ ...card, rows });
  };

  const deleteRow = (idx) => {
    const rows = card.rows.filter((_, i) => i !== idx);
    onUpdate({ ...card, rows });
  };

  const addRow = () => {
    onUpdate({ ...card, rows: [...card.rows, { label: 'Label', val: '', type: 'text' }] });
  };

  const colorMap = {
    teal: '#19a4a0', yellow: '#e8a327', red: '#ff4747',
    blue: '#2563eb', gray: '#6b7280'
  };
  const accentColor = colorMap[card.color] || colorMap.teal;

  return (
    <div className="card" style={{ '--accent': accentColor }}>
      {editMode && (
        <button className="card-delete-btn" onClick={onDelete} title="Delete card">✕</button>
      )}
      <div className="card-header">
        <div className="card-icon" style={{ background: accentColor + '18', color: accentColor }}>
          <span className="material-icons">{card.icon}</span>
        </div>
        {editMode ? (
          <input
            className="edit-input card-title-input"
            value={card.title}
            onChange={e => onUpdate({ ...card, title: e.target.value })}
          />
        ) : (
          <div className="card-title">{card.title}</div>
        )}
      </div>
      <div className="card-rows">
        {card.rows.map((row, idx) => (
          <CardRow
            key={idx}
            row={row}
            revealed={!!revealed[idx]}
            onToggleReveal={() => toggleReveal(idx)}
            editMode={editMode}
            onChange={(newRow) => updateRow(idx, newRow)}
            onDelete={() => deleteRow(idx)}
            isReadOnly={isReadOnly}
          />
        ))}
      </div>
      {editMode && (
        <button className="add-row-btn" onClick={addRow}>+ add row</button>
      )}
    </div>
  );
}

function Section({ section, editMode, onUpdate, onDelete, onAddCard, onMoveUp, onMoveDown, isReadOnly }) {
  const updateCard = (cardIdx, newCard) => {
    const cards = section.cards.map((c, i) => i === cardIdx ? newCard : c);
    onUpdate({ ...section, cards });
  };

  const deleteCard = (cardIdx) => {
    const cards = section.cards.filter((_, i) => i !== cardIdx);
    onUpdate({ ...section, cards });
  };

  return (
    <div className="section">
      <div className="section-header">
        {editMode ? (
          <input
            className="edit-input section-title-input"
            value={section.section}
            onChange={e => onUpdate({ ...section, section: e.target.value })}
          />
        ) : (
          <div className="section-title">{section.section}</div>
        )}
        {editMode && (
          <div className="section-actions">
            <button className="move-btn" onClick={onMoveUp} title="Move up">▲</button>
            <button className="move-btn" onClick={onMoveDown} title="Move down">▼</button>
            <button className="section-del-btn" onClick={onDelete}>Delete section</button>
          </div>
        )}
      </div>
      <div className="cards-grid">
        {section.cards.map((card, idx) => (
          <Card
            key={card.id || idx}
            card={card}
            editMode={editMode}
            onUpdate={(newCard) => updateCard(idx, newCard)}
            onDelete={() => deleteCard(idx)}
            isReadOnly={isReadOnly}
          />
        ))}
        {editMode && (
          <button className="add-card-btn" onClick={onAddCard}>+ add card</button>
        )}
      </div>
    </div>
  );
}

function Dashboard({ onLock, isReadOnly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const ref = doc(db, 'ops-dashboard', 'data');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setData(snap.data().sections);
        } else {
          setData(DEFAULT_DATA);
          await setDoc(ref, { sections: DEFAULT_DATA });
        }
      } catch (e) {
        console.error(e);
        setData(DEFAULT_DATA);
      }
      setLoading(false);
    };
    load();
  }, []);

  const save = useCallback(async (d) => {
    setSaving(true);
    try {
      const ref = doc(db, 'ops-dashboard', 'data');
      await setDoc(ref, { sections: d });
      setSaveMsg('Saved ✓');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (e) {
      setSaveMsg('Save failed');
    }
    setSaving(false);
  }, []);

  const updateSection = (idx, newSec) => {
    const d = data.map((s, i) => i === idx ? newSec : s);
    setData(d);
  };

  const deleteSection = (idx) => {
    setData(data.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    const d = [...data];
    const target = idx + dir;
    if (target < 0 || target >= d.length) return;
    [d[idx], d[target]] = [d[target], d[idx]];
    setData(d);
  };

  const addSection = () => {
    const newSec = {
      id: 'sec_' + Date.now(),
      section: 'New section',
      cards: []
    };
    setData([...data, newSec]);
  };

  const addCard = (secIdx) => {
    const newCard = {
      id: 'card_' + Date.now(),
      title: 'New card',
      icon: 'star',
      color: 'teal',
      rows: [{ label: 'Label', val: '', type: 'text' }]
    };
    const d = data.map((s, i) => {
      if (i !== secIdx) return s;
      return { ...s, cards: [...s.cards, newCard] };
    });
    setData(d);
  };

  const filteredData = () => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.map(sec => ({
      ...sec,
      cards: sec.cards.filter(card =>
        card.title.toLowerCase().includes(q) ||
        sec.section.toLowerCase().includes(q) ||
        card.rows.some(r => r.label.toLowerCase().includes(q) ||
          (r.type !== 'secret' && r.val.toLowerCase().includes(q)))
      )
    })).filter(sec => sec.cards.length > 0);
  };

  if (loading) return (
    <div className="loading">
      <div className="loading-spinner" />
      <p>Loading dashboard…</p>
    </div>
  );

  return (
    <div className="dashboard">
      <div className="topbar">
        <div className="topbar-logo">
          <span className="tl">L</span>itvill <span className="topbar-ops">ops</span>
        </div>
        <div className="topbar-search">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Search anything…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="topbar-actions">
          {editMode && (
            <>
              <span className="save-msg">{saveMsg}</span>
              <button
                className="btn btn-save"
                onClick={() => save(data)}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
          {!isReadOnly && (
            <button
              className={`btn ${editMode ? 'btn-done' : 'btn-edit'}`}
              onClick={() => {
                if (editMode) save(data);
                setEditMode(e => !e);
              }}
            >
              {editMode ? '✓ Done' : '✎ Edit'}
            </button>
          )}
          <button className="btn btn-lock" onClick={onLock}>🔒 Lock</button>
        </div>
      </div>

      {editMode && (
        <div className="edit-banner">
          Edit mode — changes save to Firestore when you click Save or Done
        </div>
      )}

      <div className="content">
        {filteredData().map((sec, idx) => (
          <Section
            key={sec.id || idx}
            section={sec}
            editMode={editMode}
            onUpdate={(newSec) => updateSection(idx, newSec)}
            onDelete={() => deleteSection(idx)}
            onAddCard={() => addCard(idx)}
            onMoveUp={() => moveSection(idx, -1)}
            onMoveDown={() => moveSection(idx, 1)}
            isReadOnly={isReadOnly}
          />
        ))}
        {editMode && (
          <button className="add-section-btn" onClick={addSection}>+ add section</button>
        )}
        {!editMode && filteredData().length === 0 && (
          <div className="no-results">No results for "{search}"</div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [role, setRole] = useState(null); // null | 'admin' | 'coadmin'
  if (!role) return (
    <PinScreen
      onUnlockAdmin={() => setRole('admin')}
      onUnlockCoadmin={() => setRole('coadmin')}
    />
  );
  return (
    <Dashboard
      key={role}
      onLock={() => setRole(null)}
      isReadOnly={role === 'coadmin'}
    />
  );
}
